/**
 * FinixTokenizationForm Component
 * 
 * PASS 2: Reusable React component for Finix Flex hosted fields.
 * 
 * Features:
 * - Automatic script loading
 * - Stable initialization (no re-init on state changes)
 * - Proper cleanup only on unmount or config change
 * - Loading and error states
 * - Never exposes raw card data
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { 
  loadFinixScript, 
  initializeFinixForm, 
  cleanupFinixForm 
} from '../lib/finixFlex';

// Generate unique element IDs
let instanceCounter = 0;

export default function FinixTokenizationForm({
  applicationId,
  environment = 'sandbox',
  onTokenize,
  onError,
  onReady,
  disabled = false,
  className = '',
  style = {},
}) {
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState(null);
  const [finixError, setFinixError] = useState(null); // Finix-specific errors
  
  // Stable refs for callbacks (don't trigger re-init)
  const onTokenizeRef = useRef(onTokenize);
  const onErrorRef = useRef(onError);
  const onReadyRef = useRef(onReady);
  
  // Keep refs updated
  useEffect(() => { onTokenizeRef.current = onTokenize; }, [onTokenize]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);
  
  // Form state refs
  const formInstanceRef = useRef(null);
  const isInitializingRef = useRef(false);
  const elementIdRef = useRef(`finix-form-${++instanceCounter}`);
  const isMountedRef = useRef(false);

  // Initialize form - ONLY depends on applicationId and environment
  useEffect(() => {
    isMountedRef.current = true;
    const elementId = elementIdRef.current;
    
    console.log('[FinixForm] Effect mount, elementId:', elementId);
    
    const initForm = async () => {
      // Prevent concurrent init
      if (isInitializingRef.current) {
        console.log('[FinixForm] Already initializing');
        return;
      }
      
      // Skip if form exists
      if (formInstanceRef.current) {
        console.log('[FinixForm] Form exists');
        return;
      }
      
      if (!applicationId) {
        setErrorMessage('Application ID required');
        setStatus('error');
        return;
      }

      isInitializingRef.current = true;
      setStatus('loading');
      setFinixError(null);
      
      console.log('[FinixForm] Init start, appId:', applicationId.substring(0, 6) + '...');

      try {
        await loadFinixScript();
        
        if (!isMountedRef.current) {
          isInitializingRef.current = false;
          return;
        }

        const container = document.getElementById(elementId);
        if (!container) {
          throw new Error(`Container #${elementId} not found`);
        }

        // Wait for DOM paint
        await new Promise(r => requestAnimationFrame(() => setTimeout(r, 50)));
        if (!isMountedRef.current) {
          isInitializingRef.current = false;
          return;
        }

        console.log('[FinixForm] Calling initializeFinixForm');
        const form = initializeFinixForm(elementId, {
          environment,
          applicationId,
          // Use refs for callbacks - stable references
          onTokenize: (result) => {
            console.log('[FinixForm] Token received');
            if (onTokenizeRef.current) onTokenizeRef.current(result);
          },
          onError: (err) => {
            console.log('[FinixForm] Finix error:', err.message);
            setFinixError(err);
            if (onErrorRef.current) onErrorRef.current(err);
          },
          onUpdate: (state) => {
            // Just log, don't trigger state changes that could cause re-render loops
            console.log('[FinixForm] State:', state?.hasErrors ? 'has errors' : 'valid');
          },
          style,
        });

        if (!isMountedRef.current) {
          if (form) cleanupFinixForm(elementId);
          isInitializingRef.current = false;
          return;
        }

        if (form) {
          formInstanceRef.current = form;
          console.log('[FinixForm] Ready');
          setStatus('ready');
          if (onReadyRef.current) onReadyRef.current();
        } else {
          throw new Error('PaymentForm returned null');
        }
      } catch (err) {
        console.error('[FinixForm] Init error:', err);
        if (isMountedRef.current) {
          setErrorMessage(err.message);
          setStatus('error');
        }
      } finally {
        isInitializingRef.current = false;
      }
    };

    initForm();

    // Cleanup ONLY on unmount or config change
    return () => {
      console.log('[FinixForm] Cleanup, elementId:', elementId);
      isMountedRef.current = false;
      isInitializingRef.current = false;
      if (formInstanceRef.current) {
        cleanupFinixForm(elementId);
        formInstanceRef.current = null;
      }
    };
  }, [applicationId, environment]); // ONLY these deps - not callbacks

  return (
    <div className={`finix-tokenization-form ${className}`} data-testid="finix-tokenization-form">
      {/* Loading */}
      {status === 'loading' && (
        <div className="flex items-center justify-center py-8 bg-zinc-900/80 rounded-lg" data-testid="finix-form-loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
          <span className="ml-3 text-zinc-400">Loading secure payment form...</span>
        </div>
      )}

      {/* Init Error */}
      {status === 'error' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400" data-testid="finix-form-error">
          <p className="font-medium">Payment form error</p>
          <p className="text-sm mt-1">{errorMessage}</p>
        </div>
      )}

      {/* Finix Error (shown alongside form) */}
      {finixError && status === 'ready' && (
        <div className="mb-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-400 text-sm" data-testid="finix-validation-error">
          <p className="font-medium">Validation: {finixError.code || 'ERROR'}</p>
          <p>{finixError.message}</p>
        </div>
      )}

      {/* Form Container - always in DOM except on init error */}
      {status !== 'error' && (
        <div 
          id={elementIdRef.current}
          className="finix-form-container"
          style={{ minHeight: '280px', ...style }}
          data-testid="finix-form-container"
        />
      )}
    </div>
  );
}

export { FinixTokenizationForm };
